import { Button, Descriptions, Edit, FormCard, PageHeader } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../../components'
import { styled } from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { Text } from '@lmc/ui-kit/src/general'
import useUser from 'hooks/useUser/useUser'
import dayjs from 'dayjs'
import { Spinner } from 'components/generic'
import { useAbsenceDetail } from 'screens/Rostering/hooks/useAbsenceDetail'
import { useAbsenceTypesList } from 'screens/Rostering/hooks/useAbsenceTypesList'
import {
  TIME_FIRST_DATE_FORMAT,
  getDateTimeInZone,
} from 'screens/Rostering/utils.datetime'

const IndividualAbsenceScreen = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { absenceId } = useParams()
  const { absence, isAbsenceLoading } = useAbsenceDetail({ absenceId })
  const { user, isLoading: isUserLoading } = useUser({ id: absence?.userId })
  const { absenceTypes } = useAbsenceTypesList()
  const absenceType = absenceTypes.find(
    (absenceType) => absenceType.id === absence?.absenceType,
  )
  const durationDays = Math.ceil(
    dayjs(absence?.endDate).diff(absence?.startDate, 'day', true),
  )
  const durationHours = Math.ceil(
    dayjs(absence?.endDate).diff(absence?.startDate, 'hour', true),
  )
  const durationString = durationDays
    ? durationDays === 1
      ? '1 day'
      : `${durationDays} days`
    : `${durationHours} hours`

  const absenceDescriptions = [
    {
      key: 1,
      label: <Text>{t('TEAM_MEMBER')}</Text>,
      children: (
        <Text>
          {user?.name.first} {user?.name.last}
        </Text>
      ),
    },
    {
      key: 2,
      label: <Text>{t('ABSENCE_TYPE')}</Text>,
      children: <Text>{absenceType?.name}</Text>,
    },
    {
      key: 3,
      label: <Text>{t('START_TIME')}</Text>,
      children: (
        <Text>
          {absence?.startDate
            ? getDateTimeInZone(absence.startDate).format(
                TIME_FIRST_DATE_FORMAT,
              )
            : '-'}
        </Text>
      ),
    },
    {
      key: 4,
      label: <Text>{t('END_TIME')}</Text>,
      children: (
        <Text>
          {absence?.endDate
            ? getDateTimeInZone(absence.endDate).format(TIME_FIRST_DATE_FORMAT)
            : '-'}
        </Text>
      ),
    },
    {
      key: 5,
      label: <Text>{t('DURATION')}</Text>,
      children: <Text>{durationString}</Text>,
    },
  ]

  const additionalDetailsDescriptions = [
    {
      key: 1,
      label: <Text>{t('ROTA_EVENTS_UNALLOCATED')}</Text>,
      children: <Text>{absence?.instancesUnallocated}</Text>,
    },
    {
      key: 2,
      label: <Text>{t('NOTES')}</Text>,
      children: absence?.absenceNotes ? (
        <Text>{absence?.absenceNotes} </Text>
      ) : undefined,
    },
  ]

  if (isAbsenceLoading || isUserLoading) return <Spinner />

  return (
    <>
      <PageHeader
        onBack={() => navigate(-1)}
        title={t('INDIVIDUAL_ABSENCE_TITLE', {
          user: `${user?.name.first} ${user?.name.last}`,
          type: absenceType?.name,
        })}
        subtitle={t('LAST_UPDATED_DATE_BY', {
          date: dayjs(absence?.updatedAt).format('DD-MM-YYYY HH:mm'),
          user: absence?.updatedBy.name,
          by: absence?.updatedBy.name ? '- By' : '',
        })}
        sticky
      >
        <ButtonsContainer>
          <Button variant="primary" onClick={() => navigate(`edit`)}>
            <Edit />
            {t('EDIT')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <SectionHeader title={t('INFORMATION')} />
        <CardsContainer>
          <InfoCard>
            <Descriptions
              bordered
              column={1}
              labelStyle={labelStyle}
              items={absenceDescriptions}
            />
          </InfoCard>
          <InfoCard title={t('ADDITIONAL_DETAILS')}>
            <Descriptions
              bordered
              column={1}
              labelStyle={labelStyle}
              items={additionalDetailsDescriptions.filter(
                (item) => item.children !== undefined,
              )}
            />
          </InfoCard>
        </CardsContainer>
      </PageWrapper>
    </>
  )
}

const labelStyle = {
  width: 272,
  padding: 16,
}

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InfoCard = styled(FormCard)`
  flex: 1;
  justify-content: flex-start;
`

export default IndividualAbsenceScreen
